import { useState, useRef, useCallback, useLayoutEffect, useEffect, RefObject, MutableRefObject } from 'react';

// Extra space (px) reserved between the tab row and the category tag on the right.
// Without this buffer, the last visible tab can visually overlap the category tag.
const CATEGORY_GAP_BUFFER = 56;

// Estimated width (px) of the "More ▾" button before it has been painted.
// Used on the very first measurement pass when the button's real width isn't available yet.
// Slightly over-estimated on purpose so we never show one tab too many.
const MORE_BTN_FALLBACK_WIDTH = 72;

interface TabOverflowResult {
    tabRowRef: RefObject<HTMLDivElement>;
    categoryWrapRef: RefObject<HTMLDivElement>;
    measureRefs: MutableRefObject<(HTMLDivElement | null)[]>;
    moreBtnRef: RefObject<HTMLButtonElement>;
    visibleIndices: number[];
    overflowIndices: number[];
    hasOverflow: boolean;
}

/**
 * Computes which tabs fit in the available row width and which should go into the "More" dropdown.
 *
 * - Tabs always appear in their original order.
 * - The active tab is always kept visible, even if it would otherwise overflow.
 * - Recalculates automatically when the container or category tag is resized.
 */
export function useTabOverflow(data: any[], activeTab: string): TabOverflowResult {
    // Index of the first tab that doesn't fit. Infinity = all tabs fit (initial state).
    const [overflowFrom, setOverflowFrom] = useState(Infinity);

    const tabRowRef = useRef<HTMLDivElement>(null);
    const categoryWrapRef = useRef<HTMLDivElement>(null);
    // One ref per tab, populated by the off-screen measurement container in the component.
    const measureRefs = useRef<(HTMLDivElement | null)[]>([]);
    const moreBtnRef = useRef<HTMLButtonElement>(null);

    const recompute = useCallback(() => {
        if (!tabRowRef.current || data.length === 0) return;

        // Shrink available width by a buffer when the category tag is present so tabs
        // don't run into it.
        const categoryWidth = categoryWrapRef.current?.offsetWidth ?? 0;
        const available = tabRowRef.current.offsetWidth - (categoryWidth > 0 ? CATEGORY_GAP_BUFFER : 0);
        if (available <= 0) return;

        // Read each tab's full width including its left/right margins.
        const tabWidths = data.map((_, i) => {
            const el = measureRefs.current[i];
            if (!el) return 0;
            const s = window.getComputedStyle(el);
            return el.offsetWidth + (parseFloat(s.marginLeft) || 0) + (parseFloat(s.marginRight) || 0);
        });

        // If everything fits, show all tabs with no "More" button.
        if (tabWidths.reduce((sum, w) => sum + w, 0) <= available) {
            setOverflowFrom(data.length);
            return;
        }

        // Fill tabs from left to right, always leaving room for the "More" button.
        const moreWidth = moreBtnRef.current?.offsetWidth ?? MORE_BTN_FALLBACK_WIDTH;
        let used = tabWidths[0];
        let count = 1;

        for (let i = 1; i < data.length; i++) {
            if (used + tabWidths[i] + moreWidth <= available) {
                used += tabWidths[i];
                count++;
            } else {
                break;
            }
        }

        setOverflowFrom(count);
    }, [data]);

    // Run synchronously after every render so the layout is correct before the browser paints.
    useLayoutEffect(() => { recompute(); }, [recompute]);

    // Re-run whenever the tab row or category tag changes size (e.g. modal resize).
    useEffect(() => {
        if (!tabRowRef.current) return;
        const observer = new ResizeObserver(recompute);
        observer.observe(tabRowRef.current);
        if (categoryWrapRef.current) observer.observe(categoryWrapRef.current);
        return () => observer.disconnect();
    }, [recompute]);

    const activeDataIdx = data.findIndex(d => d.text === activeTab);
    const naturalCount = isFinite(overflowFrom) ? overflowFrom : data.length;
    const hasOverflow = isFinite(overflowFrom) && overflowFrom < data.length;

    // Build the visible set in original order.
    // If the active tab would be hidden, swap it into the last visible slot.
    const visibleIndices: number[] =
        !hasOverflow || activeDataIdx < naturalCount
            ? [...Array(naturalCount).keys()]
            : [...Array(naturalCount - 1).keys(), activeDataIdx].sort((a, b) => a - b);

    const visibleSet = new Set(visibleIndices);
    const overflowIndices = data.map((_, i) => i).filter(i => !visibleSet.has(i));

    return { tabRowRef, categoryWrapRef, measureRefs, moreBtnRef, visibleIndices, overflowIndices, hasOverflow };
}
